'use client';

import { forwardRef, useEffect, useImperativeHandle } from 'react';

import { FormField, InputForm } from '@/components';
import { Select, type SelectOption } from '@/components/ui';
import { Controller } from '@/libs';
import { useCheckoutStore } from '@/stores';
import { BRAZILIAN_STATES_OPTIONS } from '@/types/enums';
import type { Address } from '@/types/models/Address';
import { formatCEP } from '@/utils';

import { useAddressForm } from './hooks';

type AddressFormProps = {
  handleNextStep: () => void;
  onValidationChange?: (isValid: boolean) => void;
};

export type AddressFormRef = {
  submit: () => void;
};

export const AddressForm = forwardRef<AddressFormRef, AddressFormProps>(
  ({ handleNextStep, onValidationChange }, ref) => {
    const addressData = useCheckoutStore((state) => state.checkoutData.addressData);
    const setAddressData = useCheckoutStore((state) => state.setAddressData);

    const handleContinue = (address: Address) => {
      setAddressData(address);
      handleNextStep();
    };

    const {
      control,
      errors,
      isValid: isAddressFormValid,
      isSearchingCep,
      submitAddressForm,
      handleCepBlur,
    } = useAddressForm({
      onContinue: handleContinue,
      isLoading: false,
      initialAddress: addressData || undefined,
    });

    useImperativeHandle(ref, () => ({
      submit: submitAddressForm,
    }));

    useEffect(() => {
      onValidationChange?.(isAddressFormValid);
    }, [isAddressFormValid, onValidationChange]);

    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 space-y-4">
          <div className="flex flex-row items-center justify-between gap-4">
            <FormField error={errors.zipCode} label="CEP" required>
              <InputForm
                control={control}
                name="zipCode"
                placeholder="00000-000"
                disabled={isSearchingCep}
                inputMode="numeric"
                formatFunction={formatCEP}
                onBlur={handleCepBlur}
              />
            </FormField>

            <FormField error={errors.state} label="Estado" required>
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Estado"
                    disabled={isSearchingCep}
                    options={BRAZILIAN_STATES_OPTIONS as SelectOption[]}
                    className="w-full"
                  />
                )}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-[1fr_0.6fr] gap-4">
            <FormField error={errors.city} label="Cidade" required>
              <InputForm
                control={control}
                name="city"
                placeholder="Cidade"
                disabled={isSearchingCep}
                inputMode="text"
              />
            </FormField>

            <FormField error={errors.neighborhood} label="Bairro" required>
              <InputForm
                control={control}
                name="neighborhood"
                placeholder="Bairro"
                disabled={isSearchingCep}
                inputMode="text"
              />
            </FormField>
          </div>

          <FormField error={errors.street} label="Logradouro" required>
            <InputForm
              control={control}
              name="street"
              placeholder="Rua/Avenida"
              disabled={isSearchingCep}
              inputMode="text"
            />
          </FormField>

          <div className="grid grid-cols-[0.65fr_1fr] gap-4">
            <FormField error={errors.number} label="Número" required>
              <InputForm
                control={control}
                name="number"
                placeholder="Número"
                disabled={isSearchingCep}
                inputMode="text"
              />
            </FormField>

            <FormField error={errors.complement} label="Complemento">
              <InputForm
                control={control}
                name="complement"
                placeholder="Opcional"
                disabled={isSearchingCep}
                inputMode="text"
              />
            </FormField>
          </div>
        </div>
      </div>
    );
  },
);

AddressForm.displayName = 'AddressForm';
